/**
 * Modal de delegación de aprobación workflow (0668).
 *
 * Sustituye el input UUID literal del legacy ActionCenterPage por un picker
 * de personas con search debounced 200ms. Si la persona NO tiene `usuarioId`
 * asociado, se muestra warning (la BD requiere userId, no personaId).
 *
 * UX:
 *   - Search box (busca por nombre + apellidos + email + código).
 *   - Lista filtrada de personas con avatar + nombre + email + puesto actual.
 *   - Click en persona → la selecciona (chip arriba).
 *   - Textarea motivo (≥ 5 chars obligatorio).
 *   - Botones Cancelar / Delegar (verde).
 */

import { useEffect, useState } from 'react';
import { useI18n } from '../../../../i18n';
import { request, type PersonaResumen } from '../../../shared/services/api';
import { useDelegarWorkflow } from '../hooks/useDelegarWorkflow';
import type { ArtefactoWorkflowTipo } from '../hooks/useActionCenterDetail';

export interface DelegarModalProps {
  tipo: ArtefactoWorkflowTipo;
  id: string;
  artefactoLabel: string;
  onClose: () => void;
  onDelegado: () => void;
}

interface PersonaConUsuario extends PersonaResumen {
  /** Usuario asociado (poblado por endpoint /apps/personas o resolver por código). */
  usuarioId?: string | null;
}

export function DelegarModal(props: DelegarModalProps) {
  const { t } = useI18n();
  const tt = t.sign.actionCenter.premium.delegar;

  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [personas, setPersonas] = useState<PersonaConUsuario[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<PersonaConUsuario | null>(null);
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useDelegarWorkflow();

  // Debounce 200ms.
  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedQ(q.trim()), 200);
    return () => window.clearTimeout(id);
  }, [q]);

  // Buscar personas tras debounce.
  useEffect(() => {
    let cancelled = false;
    if (debouncedQ.length === 0) {
      setPersonas([]);
      return () => {
        cancelled = true;
      };
    }
    setLoading(true);
    request<{ data: PersonaConUsuario[] }>(
      `/apps/personas?q=${encodeURIComponent(debouncedQ)}&pageSize=20`,
    )
      .then((res) => {
        if (cancelled) return;
        setPersonas(res.data ?? []);
      })
      .catch(() => {
        if (cancelled) return;
        setPersonas([]);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQ]);

  const handleDelegar = async () => {
    setError(null);
    if (!selected) {
      setError(tt.errorNoPersona);
      return;
    }
    if (!selected.usuarioId) {
      setError(tt.errorPersonaSinUsuario);
      return;
    }
    if (motivo.trim().length < 5) {
      setError(tt.errorMotivoCorto);
      return;
    }
    try {
      await mutation.mutateAsync({
        tipo: props.tipo,
        id: props.id,
        delegadoUserId: selected.usuarioId,
        motivo: motivo.trim(),
      });
      props.onDelegado();
    } catch (e) {
      setError(e instanceof Error ? e.message : tt.errorDelegar);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'DM Sans, sans-serif',
      }}
      role="dialog"
      aria-modal="true"
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 8,
          boxShadow: '0 16px 48px rgba(0,0,0,0.16)',
          width: '90%',
          maxWidth: 520,
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: 16,
            borderBottom: '1px solid #E5E7EB',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#1F3864' }}>{tt.title}</h3>
          <span style={{ fontSize: 12, color: '#6B7280' }}>{props.artefactoLabel}</span>
        </div>

        {/* Body */}
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            padding: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          {selected ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 12px',
                background: '#E8F0FE',
                border: '1px solid #1F3864',
                borderRadius: 6,
              }}
            >
              <strong style={{ flex: 1, fontSize: 13 }}>
                {selected.nombre} {selected.apellidos}
              </strong>
              <span style={{ fontSize: 11, color: '#6B7280' }}>{selected.email}</span>
              <button
                type="button"
                onClick={() => setSelected(null)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: '#6B7280',
                  cursor: 'pointer',
                  fontSize: 18,
                  lineHeight: 1,
                }}
                aria-label={tt.removeSelection}
              >
                ×
              </button>
            </div>
          ) : (
            <>
              <input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={tt.searchPlaceholder}
                aria-label={tt.searchPlaceholder}
                style={{
                  padding: '8px 12px',
                  fontSize: 14,
                  border: '1px solid #D1D5DB',
                  borderRadius: 6,
                  outline: 'none',
                }}
                autoFocus
              />
              <div
                style={{
                  flex: 1,
                  minHeight: 120,
                  maxHeight: 280,
                  overflow: 'auto',
                  border: '1px solid #E5E7EB',
                  borderRadius: 6,
                }}
              >
                {loading && (
                  <div style={{ padding: 16, fontSize: 13, color: '#6B7280' }}>{tt.loading}</div>
                )}
                {!loading && debouncedQ.length === 0 && (
                  <div style={{ padding: 16, fontSize: 13, color: '#9CA3AF' }}>{tt.searchHint}</div>
                )}
                {!loading && debouncedQ.length > 0 && personas.length === 0 && (
                  <div style={{ padding: 16, fontSize: 13, color: '#9CA3AF' }}>{tt.noResults}</div>
                )}
                {!loading &&
                  personas.map((p) => (
                    <button
                      type="button"
                      key={p.id}
                      onClick={() => setSelected(p)}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        width: '100%',
                        padding: '8px 12px',
                        textAlign: 'left',
                        border: 'none',
                        background: 'transparent',
                        borderBottom: '1px solid #F3F4F6',
                        cursor: 'pointer',
                      }}
                    >
                      <strong style={{ fontSize: 13, color: '#1F2937' }}>
                        {p.nombre} {p.apellidos}
                      </strong>
                      <span style={{ fontSize: 11, color: '#6B7280' }}>
                        {p.email}
                        {p.puestoActual?.puesto?.titulo ? ` · ${p.puestoActual.puesto.titulo}` : ''}
                      </span>
                    </button>
                  ))}
              </div>
            </>
          )}

          <textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder={tt.motivoPlaceholder}
            aria-label={tt.motivoLabel}
            rows={3}
            style={{
              padding: 8,
              fontSize: 13,
              border: '1px solid #D1D5DB',
              borderRadius: 6,
              resize: 'vertical',
              outline: 'none',
              fontFamily: 'DM Sans, sans-serif',
            }}
          />
          {error && (
            <span style={{ color: '#991B1B', fontSize: 12 }} role="alert">
              {error}
            </span>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: 12,
            borderTop: '1px solid #E5E7EB',
            background: '#FAFAFA',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
          }}
        >
          <button
            type="button"
            onClick={props.onClose}
            disabled={mutation.isPending}
            style={{
              padding: '8px 16px',
              fontSize: 13,
              fontWeight: 600,
              color: '#374151',
              background: '#E5E7EB',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
            }}
          >
            {tt.cancel}
          </button>
          <button
            type="button"
            onClick={handleDelegar}
            disabled={mutation.isPending || !selected}
            style={{
              padding: '8px 16px',
              fontSize: 13,
              fontWeight: 600,
              color: '#fff',
              background: '#10B981',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              opacity: !selected ? 0.5 : 1,
            }}
          >
            {mutation.isPending ? tt.delegating : tt.confirm}
          </button>
        </div>
      </div>
    </div>
  );
}
